import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
} from "react";
import { Client } from "../@data";
import {
  getClientFromLocalStorage,
  saveClientToLocalStorage,
  clearClientFromLocalStorage,
} from "../utils/localStorage";
import {
  verifyClientPassword,
  updateClientPassword,
  getClientByPhone,
} from "../services/clients";
import { useNotification } from "./notification";

export interface AuthContextModel {
  client: Client;
  isLogged: boolean;
  signOut: () => void;
  setClient: (client: Client) => void;
  // Funcionalidades de senha
  hasPassword: boolean;
  verifyPassword: (password: string) => boolean;
  createPassword: (password: string) => Promise<boolean>;
  updatePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<boolean>;
  removePassword: (currentPassword: string) => Promise<boolean>;
  refreshClientData: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextModel>(
  {} as AuthContextModel
);

export const AuthProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [client, setClient] = useState<Client>({} as Client);
  const { handleNotification } = useNotification();

  const isLogged = !!client?.id;
  const hasPassword = !!client?.password;

  const signOut = () => {
    setClient({} as Client);
    clearClientFromLocalStorage();
  };

  const setClientData = (clientData: Client) => {
    setClient(clientData);
    saveClientToLocalStorage(clientData);
  };

  const refreshClientData = async () => {
    try {
      if (client.phone) {
        const updatedClient = await getClientByPhone(client.phone);
        if (updatedClient) {
          setClientData(updatedClient);
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar dados do cliente:", error);
    }
  };

  const verifyPassword = (password: string) => {
    return verifyClientPassword(client, password);
  };

  const createPassword = async (password: string) => {
    try {
      await updateClientPassword(client.id, password);
      await refreshClientData();
      handleNotification("success", "Senha criada com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao criar senha:", error);
      handleNotification("error", "Erro ao criar senha");
      return false;
    }
  };

  const updatePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      // Verifica se a senha atual está correta
      if (!verifyClientPassword(client, currentPassword)) {
        handleNotification("error", "Senha atual incorreta");
        return false;
      }

      await updateClientPassword(client.id, newPassword);
      await refreshClientData();
      handleNotification("success", "Senha atualizada com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao atualizar senha:", error);
      handleNotification("error", "Erro ao atualizar senha");
      return false;
    }
  };

  const removePassword = async (currentPassword: string) => {
    try {
      // Verifica se a senha atual está correta
      if (!verifyClientPassword(client, currentPassword)) {
        handleNotification("error", "Senha atual incorreta");
        return false;
      }

      // Remove a senha definindo como undefined
      await updateClientPassword(client.id, "");
      await refreshClientData();
      handleNotification("success", "Senha removida com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao remover senha:", error);
      handleNotification("error", "Erro ao remover senha");
      return false;
    }
  };

  useEffect(() => {
    const savedClient = getClientFromLocalStorage();
    if (savedClient) {
      setClient(savedClient);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        client,
        isLogged,
        signOut,
        setClient: setClientData,
        hasPassword,
        verifyPassword,
        createPassword,
        updatePassword,
        removePassword,
        refreshClientData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
